import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, Paintbrush, ShieldCheck, Puzzle, Save, Upload, ExternalLink, Lock, RefreshCw, Receipt, Mail, FileText } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../lib/errors';
import AuditEventTable from '../../components/audit/AuditEventTable';
import {
    getCompanySettings,
    updateCompanySettings,
    uploadCompanyLogo,
    uploadLoginBackground,
    getAzureConfig,
    getCompanyAuthSettings,
    updateCompanyAuthSettings,
    getOwnerAuthEvents,
} from '../../lib/api';

// ─── Company Profile Tab ──────────────────────────────────────────────────────

function CompanyProfileTab() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: '',
        legal_address: '',
        org_number: '',
        vat_number: '',
        phone: '',
        email: '',
        website: '',
    });

    useEffect(() => {
        const fetchSettings = async () => {
            setLoading(true);
            try {
                const res = await getCompanySettings();
                const data = res.data ?? {};
                setForm({
                    name: data.name ?? '',
                    legal_address: data.legal_address ?? '',
                    org_number: data.org_number ?? '',
                    vat_number: data.vat_number ?? '',
                    phone: data.phone ?? '',
                    email: data.email ?? '',
                    website: data.website ?? '',
                });
            } catch {
                toast.error(t('company.load_error'));
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [t]);

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // name and org_number are registration-locked — backend ignores them;
            // send only the owner-editable fields
            const { name, org_number, ...editable } = form;
            await updateCompanySettings(editable);
            toast.success(t('company.saved'));
        } catch (err) {
            toast.error(err.response?.data?.detail ?? t('company.save_error'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="company-profile-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-primary" />
                        {t('company.profile')}
                    </CardTitle>
                    <CardDescription>{t('company.profile_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                    {/* Read-only company ID */}
                    <div>
                        <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                            {t('company.company_id_label')}
                        </Label>
                        <Input
                            value={user?.company_id ?? '—'}
                            readOnly
                            disabled
                            className="font-mono bg-muted/40"
                            data-testid="company-id-display"
                        />
                        <p className="text-xs text-muted-foreground mt-1">{t('company.company_id_hint')}</p>
                    </div>

                    {/* Registration-locked fields — set at company creation by super admin */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-md bg-muted/30 p-4 border border-dashed">
                        <div>
                            <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.name')}
                                <span className="ml-2 text-[9px] bg-muted px-1 py-0.5 rounded">{t('company.registration_locked')}</span>
                            </Label>
                            <Input
                                value={form.name}
                                readOnly
                                disabled
                                className="bg-muted/40 font-medium"
                                data-testid="company-name-display"
                            />
                        </div>

                        <div>
                            <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.org_number')}
                                <span className="ml-2 text-[9px] bg-muted px-1 py-0.5 rounded">{t('company.registration_locked')}</span>
                            </Label>
                            <Input
                                value={form.org_number}
                                readOnly
                                disabled
                                className="bg-muted/40 font-mono"
                                data-testid="company-org-number-display"
                            />
                            <p className="text-xs text-muted-foreground mt-1">{t('company.org_number_hint')}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                        <div>
                            <Label htmlFor="company-vat" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.vat_number')}
                            </Label>
                            <Input
                                id="company-vat"
                                value={form.vat_number}
                                onChange={handleChange('vat_number')}
                                placeholder="SE556000000001"
                                data-testid="company-vat-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="company-phone" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.phone')}
                            </Label>
                            <Input
                                id="company-phone"
                                type="tel"
                                value={form.phone}
                                onChange={handleChange('phone')}
                                placeholder="+46 8 000 000"
                                data-testid="company-phone-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="company-email" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.email')}
                            </Label>
                            <Input
                                id="company-email"
                                type="email"
                                value={form.email}
                                onChange={handleChange('email')}
                                placeholder="info@company.com"
                                data-testid="company-email-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="company-website" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.website')}
                            </Label>
                            <Input
                                id="company-website"
                                type="url"
                                value={form.website}
                                onChange={handleChange('website')}
                                placeholder="https://example.com"
                                data-testid="company-website-input"
                            />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="company-address" className="text-xs uppercase mb-1 block text-muted-foreground">
                            {t('company.legal_address')}
                        </Label>
                        <textarea
                            id="company-address"
                            value={form.legal_address}
                            onChange={handleChange('legal_address')}
                            rows={3}
                            placeholder={t('company.address_placeholder')}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                            data-testid="company-address-input"
                        />
                    </div>

                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-company-profile-btn"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? t('common.saving') : t('company.save')}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}

// ─── Invoicing Tab (Task 21.3) ────────────────────────────────────────────────

const INVOICING_DEFAULT_FORM = {
    iban: '',
    bic: '',
    bankgiro: '',
    plusgiro: '',
    bank_name: '',
    approved_for_f_tax: false,
    invoice_number_prefix: '',
    invoice_number_next: 1,
    default_payment_terms_days: 30,
    default_overdue_interest_pct: 0,
    default_our_reference: '',
    default_vat_rate: 25,
    invoice_footer_text: '',
    default_header_note: '',
    default_intro_text: '',
    default_notes: '',
    default_footer_note: '',
    ocr_reference_enabled: false,
    ocr_format: 'bankgiro_luhn',
    invoice_sender_name: '',
    invoice_sender_email: '',
    invoice_reply_to: '',
};

// models/invoice.py TEXT_BLOCK_MAX_LENGTH — the cap the backend enforces on
// each of the four blocks and on these templates for them.
const TEXT_BLOCK_MAX_LENGTH = 4000;

/**
 * The four invoice text-block defaults, IN THE ORDER THE PDF PRINTS THE BLOCKS
 * THEY SEED (services/invoice_pdf.py):
 *
 *   invoice details → header_note → intro_text → LINE TABLE → notes → totals
 *   → footer_note → the company footer line
 *
 * These are templates for a NEW draft only (models/invoice.py
 * `resolve_text_blocks`). Editing one never reaches an invoice that already
 * exists — the copy or the label must not let an owner believe otherwise.
 */
const TEXT_BLOCK_DEFAULTS = [
    { field: 'default_header_note', rows: 2 },
    { field: 'default_intro_text', rows: 3 },
    { field: 'default_notes', rows: 3 },
    { field: 'default_footer_note', rows: 2 },
];

// Numeric *preference* fields the backend types as int/float. A cleared
// <Input type="number"> reports e.target.value === '' — coerced to a JS
// Number that would be NaN, or left as '' — either of which the backend
// rejects with a 422. These three are genuine preferences with sensible
// defaults, so a cleared field is coerced back to the same default used
// when loading settings (INVOICING_DEFAULT_FORM is the single source of
// truth for those numbers) rather than sending ''/NaN.
//
// invoice_number_next is DELIBERATELY excluded from this list: it seeds a
// gapless, legally-mandated invoice series, not a preference. Silently
// resetting a cleared field to 1 could reissue an already-used number (on a
// fresh tenant, before any invoice exists, there is nothing to guard
// against — the wrong series start would be saved without any error at
// all). A cleared/invalid invoice_number_next must block the save instead
// — see the validation at the top of handleSave.
const NUMERIC_FIELD_KEYS = [
    'default_payment_terms_days',
    'default_overdue_interest_pct',
    'default_vat_rate',
];

function InvoicingTab() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(INVOICING_DEFAULT_FORM);
    // invoice_bcc is edited as a comma-separated string and split/joined at the boundary.
    const [bccInput, setBccInput] = useState('');

    /**
     * True when the settings fetch failed, so the form below is showing
     * INVOICING_DEFAULT_FORM rather than this tenant's stored settings.
     *
     * That distinction is destructive, not cosmetic: bank details, the invoice
     * number series and the invoice sender all print on a legal document, and
     * saving the untouched defaults over them would overwrite real data with
     * placeholders — silently, because the form looks perfectly filled in. A
     * toast alone is not enough (it disappears, and it does not stop a click),
     * so this state survives past `setLoading(false)`, blocks `handleSave`, and
     * disables the Save button with an explanation to reload.
     *
     * It also composes with `loadedNumberNextRef`: a failed load leaves that
     * snapshot null, which the save path already reads as "no trustworthy
     * snapshot". The save can never run in that state now, so the two agree.
     */
    const [loadFailed, setLoadFailed] = useState(false);

    /**
     * `invoice_number_next` exactly as the server had it when this page loaded.
     *
     * The number series moves on its own: every invoice issued anywhere in the
     * tenant advances it. So the value this form loaded goes stale the moment
     * somebody issues an invoice, and re-posting it unchanged asks the backend
     * to wind the series BACKWARDS — which its reissue guard rightly refuses
     * with a 400. The admin who was only editing a bankgiro then gets an error
     * about invoice numbering they never touched.
     *
     * A ref rather than state: nothing renders from it, and it must not be
     * refreshed by re-renders — it is the snapshot the save compares against.
     * Null means the load failed and there is no snapshot to trust, in which
     * case the value is sent rather than assumed unchanged.
     */
    const loadedNumberNextRef = useRef(null);

    useEffect(() => {
        const fetchSettings = async () => {
            setLoading(true);
            try {
                const res = await getCompanySettings();
                const data = res.data ?? {};
                setForm({
                    iban: data.iban ?? '',
                    bic: data.bic ?? '',
                    bankgiro: data.bankgiro ?? '',
                    plusgiro: data.plusgiro ?? '',
                    bank_name: data.bank_name ?? '',
                    approved_for_f_tax: data.approved_for_f_tax ?? false,
                    invoice_number_prefix: data.invoice_number_prefix ?? '',
                    invoice_number_next: data.invoice_number_next ?? 1,
                    default_payment_terms_days: data.default_payment_terms_days ?? 30,
                    default_overdue_interest_pct: data.default_overdue_interest_pct ?? 0,
                    default_our_reference: data.default_our_reference ?? '',
                    default_vat_rate: data.default_vat_rate ?? 25,
                    invoice_footer_text: data.invoice_footer_text ?? '',
                    default_header_note: data.default_header_note ?? '',
                    default_intro_text: data.default_intro_text ?? '',
                    default_notes: data.default_notes ?? '',
                    default_footer_note: data.default_footer_note ?? '',
                    ocr_reference_enabled: data.ocr_reference_enabled ?? false,
                    ocr_format: data.ocr_format ?? 'bankgiro_luhn',
                    invoice_sender_name: data.invoice_sender_name ?? '',
                    invoice_sender_email: data.invoice_sender_email ?? '',
                    invoice_reply_to: data.invoice_reply_to ?? '',
                });
                setBccInput((data.invoice_bcc ?? []).join(', '));
                // Snapshot taken from the RESPONSE, not from form state, so it
                // is the server's value and not one of our fallbacks.
                loadedNumberNextRef.current = data.invoice_number_next ?? null;
            } catch {
                // Latch the failure as well as toasting it — the form is now
                // showing defaults, and saving those would overwrite the
                // tenant's real settings. See the loadFailed docblock above.
                setLoadFailed(true);
                loadedNumberNextRef.current = null;
                toast.error(t('company.invoicing.load_error'));
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [t]);

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleNumberChange = (field) => (e) => {
        const raw = e.target.value;
        setForm((prev) => ({ ...prev, [field]: raw === '' ? '' : Number(raw) }));
    };

    const handleToggle = (field) => (checked) => {
        setForm((prev) => ({ ...prev, [field]: checked }));
    };

    const handleSave = async () => {
        // The settings never loaded, so this form holds defaults rather than
        // the tenant's stored values. Saving would silently replace real bank
        // details, invoice numbering and sender with placeholders. Refuse —
        // the button is disabled too, this is the belt to that pair of braces.
        if (loadFailed) {
            toast.error(t('company.invoicing.load_failed_blocked'));
            return;
        }

        // invoice_number_next seeds a legally-mandated invoice series — never
        // silently substitute a default for it. Block the save and make the
        // user type a real number instead (matches this app's existing
        // pre-submit-validation pattern of toast + early return).
        const seq = form.invoice_number_next;
        const seqIsValid =
            seq !== '' && seq !== null && !Number.isNaN(seq) &&
            Number.isInteger(seq) && seq >= 1;
        if (!seqIsValid) {
            toast.error(t('company.invoicing.invoice_number_next_required'));
            return;
        }

        setSaving(true);
        try {
            const invoice_bcc = bccInput
                .split(',')
                .map((addr) => addr.trim())
                .filter(Boolean);

            // A cleared numeric input is still '' in form state — never send
            // that to the backend (int/float fields reject it with a 422).
            // Fall back to the same defaults used when loading settings.
            // (invoice_number_next is excluded — validated above instead.)
            const normalized = { ...form };
            for (const field of NUMERIC_FIELD_KEYS) {
                const value = normalized[field];
                if (value === '' || value === null || Number.isNaN(value)) {
                    normalized[field] = INVOICING_DEFAULT_FORM[field];
                }
            }
            setForm(normalized);

            const payload = { ...normalized, invoice_bcc };

            // Send `invoice_number_next` ONLY when the admin actually changed
            // it. Unchanged, the loaded value may already be behind a series
            // that moved while this page was open, and re-posting it would
            // fail the backend's reissue guard with a 400 about numbering on a
            // save that was really about a bank detail. Omitting it leaves the
            // stored series untouched, which is exactly what "I did not edit
            // this field" means. When the admin DOES edit it the new value is
            // sent and the guard judges it on its merits — a deliberate change
            // must still be able to be refused.
            if (
                loadedNumberNextRef.current !== null &&
                payload.invoice_number_next === loadedNumberNextRef.current
            ) {
                delete payload.invoice_number_next;
            }

            await updateCompanySettings(payload);

            // The save succeeded, so whatever was sent is now the stored value
            // — move the snapshot with it, or a second save straight after
            // editing the series would re-send an unedited field all over
            // again. (When it was omitted the snapshot is already correct.)
            if ('invoice_number_next' in payload) {
                loadedNumberNextRef.current = payload.invoice_number_next;
            }

            toast.success(t('company.invoicing.saved'));
        } catch (err) {
            toast.error(getApiErrorMessage(err, t('company.invoicing.save_error')));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="company-invoicing-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-primary" />
                        {t('company.invoicing.section_title')}
                    </CardTitle>
                    <CardDescription>{t('company.invoicing.section_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="invoicing-iban" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.iban')}
                            </Label>
                            <Input
                                id="invoicing-iban"
                                value={form.iban}
                                onChange={handleChange('iban')}
                                placeholder="SE45 5000 0000 0583 9825 7466"
                                className="font-mono"
                                data-testid="invoicing-iban-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoicing-bic" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.bic')}
                            </Label>
                            <Input
                                id="invoicing-bic"
                                value={form.bic}
                                onChange={handleChange('bic')}
                                placeholder="ESSESESS"
                                className="font-mono"
                                data-testid="invoicing-bic-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoicing-bankgiro" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.bankgiro')}
                            </Label>
                            <Input
                                id="invoicing-bankgiro"
                                value={form.bankgiro}
                                onChange={handleChange('bankgiro')}
                                placeholder="123-4567"
                                data-testid="invoicing-bankgiro-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoicing-plusgiro" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.plusgiro')}
                            </Label>
                            <Input
                                id="invoicing-plusgiro"
                                value={form.plusgiro}
                                onChange={handleChange('plusgiro')}
                                placeholder="12 34 56-7"
                                data-testid="invoicing-plusgiro-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoicing-bank-name" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.bank_name')}
                            </Label>
                            <Input
                                id="invoicing-bank-name"
                                value={form.bank_name}
                                onChange={handleChange('bank_name')}
                                data-testid="invoicing-bank-name-input"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 pt-2 border-t">
                        <Switch
                            id="invoicing-f-tax"
                            checked={form.approved_for_f_tax}
                            onCheckedChange={handleToggle('approved_for_f_tax')}
                            data-testid="invoicing-f-tax-switch"
                        />
                        <div>
                            <Label htmlFor="invoicing-f-tax" className="text-sm cursor-pointer">
                                {t('company.invoicing.approved_for_f_tax')}
                            </Label>
                            <p className="text-xs text-muted-foreground">{t('company.invoicing.approved_for_f_tax_hint')}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t">
                        <div>
                            <Label htmlFor="invoicing-number-prefix" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.invoice_number_prefix')}
                            </Label>
                            <Input
                                id="invoicing-number-prefix"
                                value={form.invoice_number_prefix}
                                onChange={handleChange('invoice_number_prefix')}
                                placeholder="HI-"
                                data-testid="invoicing-number-prefix-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoicing-number-next" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.invoice_number_next')}
                            </Label>
                            <Input
                                id="invoicing-number-next"
                                type="number"
                                min={1}
                                value={form.invoice_number_next}
                                onChange={handleNumberChange('invoice_number_next')}
                                data-testid="invoicing-number-next-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">{t('company.invoicing.invoice_number_next_hint')}</p>
                        </div>
                        <div>
                            <Label htmlFor="invoicing-payment-terms" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.default_payment_terms_days')}
                            </Label>
                            <Input
                                id="invoicing-payment-terms"
                                type="number"
                                min={0}
                                value={form.default_payment_terms_days}
                                onChange={handleNumberChange('default_payment_terms_days')}
                                data-testid="invoicing-payment-terms-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoicing-interest" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.default_overdue_interest_pct')}
                            </Label>
                            <Input
                                id="invoicing-interest"
                                type="number"
                                min={0}
                                step="0.1"
                                value={form.default_overdue_interest_pct}
                                onChange={handleNumberChange('default_overdue_interest_pct')}
                                data-testid="invoicing-interest-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoicing-vat-rate" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.default_vat_rate')}
                            </Label>
                            <Input
                                id="invoicing-vat-rate"
                                type="number"
                                min={0}
                                step="0.1"
                                value={form.default_vat_rate}
                                onChange={handleNumberChange('default_vat_rate')}
                                data-testid="invoicing-vat-rate-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoicing-our-reference" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.default_our_reference')}
                            </Label>
                            <Input
                                id="invoicing-our-reference"
                                value={form.default_our_reference}
                                onChange={handleChange('default_our_reference')}
                                data-testid="invoicing-our-reference-input"
                            />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="invoicing-footer-text" className="text-xs uppercase mb-1 block text-muted-foreground">
                            {t('company.invoicing.invoice_footer_text')}
                        </Label>
                        <textarea
                            id="invoicing-footer-text"
                            value={form.invoice_footer_text}
                            onChange={handleChange('invoice_footer_text')}
                            rows={2}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                            data-testid="invoicing-footer-text-input"
                        />
                    </div>

                    <div className="flex items-center gap-3 pt-2 border-t">
                        <Switch
                            id="invoicing-ocr"
                            checked={form.ocr_reference_enabled}
                            onCheckedChange={handleToggle('ocr_reference_enabled')}
                            data-testid="invoicing-ocr-switch"
                        />
                        <div>
                            <Label htmlFor="invoicing-ocr" className="text-sm cursor-pointer">
                                {t('company.invoicing.ocr_reference_enabled')}
                            </Label>
                            <p className="text-xs text-muted-foreground">{t('company.invoicing.ocr_reference_enabled_hint')}</p>
                        </div>
                    </div>

                    {form.ocr_reference_enabled && (
                        <div className="ml-8 max-w-sm">
                            <Label htmlFor="invoicing-ocr-format" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoicing.ocr_format')}
                            </Label>
                            <Select
                                value={form.ocr_format || 'bankgiro_luhn'}
                                onValueChange={(val) => setForm((prev) => ({ ...prev, ocr_format: val }))}
                            >
                                <SelectTrigger id="invoicing-ocr-format" className="w-full" data-testid="invoicing-ocr-format-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bankgiro_luhn">{t('company.invoicing.ocr_format_bankgiro_luhn')}</SelectItem>
                                    <SelectItem value="invoice_number">{t('company.invoicing.ocr_format_invoice_number')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">{t('company.invoicing.ocr_format_hint')}</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* The four custom text blocks' company defaults (PRD 10.14).
                Their own card, not squeezed into Invoicing above, because they
                need the two things stated plainly: what order they print in,
                and that they seed NEW drafts only. */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        {t('company.invoicing.text_blocks_title')}
                    </CardTitle>
                    <CardDescription>{t('company.invoicing.text_blocks_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                    <p className="text-xs text-muted-foreground">{t('company.invoicing.text_blocks_order')}</p>
                    <p className="text-xs text-muted-foreground">{t('company.invoicing.text_blocks_hint')}</p>
                    {TEXT_BLOCK_DEFAULTS.map(({ field, rows }) => (
                        <div key={field}>
                            <Label
                                htmlFor={`invoicing-${field.replace(/_/g, '-')}`}
                                className="text-xs uppercase mb-1 block text-muted-foreground"
                            >
                                {t(`company.invoicing.${field}`)}
                            </Label>
                            <textarea
                                id={`invoicing-${field.replace(/_/g, '-')}`}
                                value={form[field]}
                                onChange={handleChange(field)}
                                rows={rows}
                                maxLength={TEXT_BLOCK_MAX_LENGTH}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                                data-testid={`invoicing-${field.replace(/_/g, '-')}-input`}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                {t(`company.invoicing.${field}_hint`)}
                            </p>
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Mail className="w-5 h-5 text-primary" />
                        {t('company.invoice_email.section_title')}
                    </CardTitle>
                    <CardDescription>{t('company.invoice_email.section_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                    <p className="text-xs text-muted-foreground">{t('company.invoice_email.fallback_hint')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="invoice-email-sender-name" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoice_email.sender_name')}
                            </Label>
                            <Input
                                id="invoice-email-sender-name"
                                value={form.invoice_sender_name}
                                onChange={handleChange('invoice_sender_name')}
                                placeholder="Hisland"
                                data-testid="invoice-email-sender-name-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoice-email-sender-email" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoice_email.sender_email')}
                            </Label>
                            <Input
                                id="invoice-email-sender-email"
                                type="email"
                                value={form.invoice_sender_email}
                                onChange={handleChange('invoice_sender_email')}
                                placeholder="faktura@hisland.com"
                                data-testid="invoice-email-sender-email-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoice-email-reply-to" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoice_email.reply_to')}
                            </Label>
                            <Input
                                id="invoice-email-reply-to"
                                type="email"
                                value={form.invoice_reply_to}
                                onChange={handleChange('invoice_reply_to')}
                                placeholder="ekonomi@hisland.com"
                                data-testid="invoice-email-reply-to-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="invoice-email-bcc" className="text-xs uppercase mb-1 block text-muted-foreground">
                                {t('company.invoice_email.bcc')}
                            </Label>
                            <Input
                                id="invoice-email-bcc"
                                value={bccInput}
                                onChange={(e) => setBccInput(e.target.value)}
                                placeholder="bokforing@hisland.com, vd@hisland.com"
                                data-testid="invoice-email-bcc-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">{t('company.invoice_email.bcc_hint')}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Saving is impossible while the load failed — say why, in place,
                rather than leaving the user to guess at a dead button. */}
            {loadFailed && (
                <p
                    className="text-sm text-destructive"
                    role="alert"
                    id="invoicing-load-failed-notice"
                    data-testid="company-invoicing-load-failed"
                >
                    {t('company.invoicing.load_failed_notice')}
                </p>
            )}

            <Button
                onClick={handleSave}
                disabled={saving || loadFailed}
                aria-describedby={loadFailed ? 'invoicing-load-failed-notice' : undefined}
                data-testid="save-company-invoicing-btn"
            >
                <Save className="w-4 h-4 mr-2" />
                {saving ? t('common.saving') : t('company.invoicing.save')}
            </Button>
        </div>
    );
}

// ─── Branding Tab ─────────────────────────────────────────────────────────────

function BrandingTab() {
    const { t } = useTranslation();
    const [logoUploading, setLogoUploading] = useState(false);
    const [bgUploading, setBgUploading] = useState(false);
    const [logoKey, setLogoKey] = useState(0);
    const [bgKey, setBgKey] = useState(0);

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const logoUrl = `${API_URL}/api/company/logo`;
    const bgUrl = `${API_URL}/api/company/login-background`;

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoUploading(true);
        try {
            await uploadCompanyLogo(file);
            toast.success(t('company.upload_success'));
            setLogoKey((k) => k + 1);
        } catch (err) {
            toast.error(err.response?.data?.detail ?? t('company.upload_error'));
        } finally {
            setLogoUploading(false);
            e.target.value = '';
        }
    };

    const handleBgUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBgUploading(true);
        try {
            await uploadLoginBackground(file);
            toast.success(t('company.upload_success'));
            setBgKey((k) => k + 1);
        } catch (err) {
            toast.error(err.response?.data?.detail ?? t('company.upload_error'));
        } finally {
            setBgUploading(false);
            e.target.value = '';
        }
    };

    return (
        <div className="space-y-6" data-testid="company-branding-tab">
            {/* Logo */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Paintbrush className="w-5 h-5 text-primary" />
                        {t('company.logo')}
                    </CardTitle>
                    <CardDescription>{t('company.logo_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="border rounded-lg p-3 bg-muted/30 flex items-center justify-center min-w-[120px] min-h-[80px]">
                            <img
                                key={logoKey}
                                src={logoUrl}
                                alt={t('company.logo')}
                                className="max-h-16 max-w-[200px] object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">{t('company.logo_hint')}</p>
                            <Label
                                htmlFor="logo-upload"
                                className="inline-flex items-center gap-2 cursor-pointer"
                            >
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={logoUploading}
                                    onClick={() => document.getElementById('logo-upload').click()}
                                    data-testid="upload-logo-btn"
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    {logoUploading ? t('company.uploading') : t('company.upload')}
                                </Button>
                            </Label>
                            <input
                                id="logo-upload"
                                type="file"
                                accept="image/*"
                                className="sr-only"
                                onChange={handleLogoUpload}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Login Background */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('company.login_background')}
                    </CardTitle>
                    <CardDescription>{t('company.login_background_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="border rounded-lg overflow-hidden w-32 h-20 bg-muted/30 flex-shrink-0">
                            <img
                                key={bgKey}
                                src={bgUrl}
                                alt={t('company.login_background')}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">{t('company.login_background_hint')}</p>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={bgUploading}
                                onClick={() => document.getElementById('bg-upload').click()}
                                data-testid="upload-bg-btn"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                {bgUploading ? t('company.uploading') : t('company.upload')}
                            </Button>
                            <input
                                id="bg-upload"
                                type="file"
                                accept="image/*"
                                className="sr-only"
                                onChange={handleBgUpload}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ─── Auth & Access Tab ────────────────────────────────────────────────────────

function AuthAccessTab() {
    const { t } = useTranslation();
    const [azureConfig, setAzureConfig] = useState(null);
    const [authSettings, setAuthSettings] = useState({ email_login_enabled: true, ms365_login_enabled: true });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getAzureConfig().then(r => setAzureConfig(r.data)).catch(() => {});
        getCompanyAuthSettings().then(r => setAuthSettings(r.data)).catch(() => {});
    }, []);

    const toggle = async (field) => {
        const next = { ...authSettings, [field]: !authSettings[field] };
        setSaving(true);
        try {
            const res = await updateCompanyAuthSettings({ [field]: next[field] });
            setAuthSettings(res.data);
            toast.success(t('company.auth_saved'));
        } catch {
            toast.error(t('company.save_error'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="company-auth-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                        {t('company.auth')}
                    </CardTitle>
                    <CardDescription>{t('company.auth_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{t('company.auth_toggle_note')}</p>

                    {/* Microsoft 365 SSO */}
                    <div className="flex items-center justify-between py-3 px-4 rounded-md border" data-testid="auth-method-microsoft_sso">
                        <div>
                            <p className="text-sm font-medium">{t('company.auth_ms365_sso')}</p>
                            {!azureConfig?.enabled && (
                                <p className="text-xs text-muted-foreground mt-0.5">{t('company.auth_ms365_not_configured')}</p>
                            )}
                        </div>
                        <Button
                            size="sm"
                            variant={authSettings.ms365_login_enabled ? 'default' : 'outline'}
                            onClick={() => toggle('ms365_login_enabled')}
                            disabled={saving || !azureConfig?.enabled}
                            data-testid="toggle-ms365"
                        >
                            {authSettings.ms365_login_enabled ? t('company.auth_enabled') : t('company.auth_disabled')}
                        </Button>
                    </div>

                    {/* Email / Password */}
                    <div className="flex items-center justify-between py-3 px-4 rounded-md border" data-testid="auth-method-email_password">
                        <p className="text-sm font-medium">{t('company.auth_email_password')}</p>
                        <Button
                            size="sm"
                            variant={authSettings.email_login_enabled ? 'default' : 'outline'}
                            onClick={() => toggle('email_login_enabled')}
                            disabled={saving}
                            data-testid="toggle-email"
                        >
                            {authSettings.email_login_enabled ? t('company.auth_enabled') : t('company.auth_disabled')}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ─── Integrations Tab ─────────────────────────────────────────────────────────

function IntegrationsTab() {
    const { t } = useTranslation();

    return (
        <div className="space-y-6" data-testid="company-integrations-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Puzzle className="w-5 h-5 text-primary" />
                        {t('company.integrations')}
                    </CardTitle>
                    <CardDescription>{t('company.integrations_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                    <p className="text-sm text-muted-foreground">
                        {t('company.integrations_managed_notice')}
                    </p>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between py-3 px-4 rounded-md border">
                            <div>
                                <p className="text-sm font-medium">{t('company.integration_fortnox')}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {t('company.integration_fortnox_hint')}
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                asChild
                                data-testid="link-to-fortnox-settings"
                            >
                                <Link to="/dashboard/settings">
                                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                                    {t('company.integration_configure')}
                                </Link>
                            </Button>
                        </div>

                        <div className="flex items-center justify-between py-3 px-4 rounded-md border">
                            <div>
                                <p className="text-sm font-medium">{t('company.integration_brevo')}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {t('company.integration_brevo_hint')}
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                asChild
                                data-testid="link-to-notifications-settings"
                            >
                                <Link to="/dashboard/settings">
                                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                                    {t('company.integration_configure')}
                                </Link>
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ─── Security Tab ─────────────────────────────────────────────────────────────

const AUDIT_EVENT_TYPES = [
    'login_success',
    'login_fail',
    'login_locked',
    'password_change',
    'role_change',
    'account_deleted',
    'lead.submitted',
    'impersonation_start',
    'company_deleted',
];

function SecurityTab() {
    const { t } = useTranslation();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eventTypeFilter, setEventTypeFilter] = useState('');

    const fetchEvents = useCallback(async (filterValue) => {
        setLoading(true);
        try {
            const params = {};
            if (filterValue) params.event_type = filterValue;
            const res = await getOwnerAuthEvents(params);
            setEvents(res.data ?? []);
        } catch {
            toast.error(t('company.load_error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchEvents('');
    }, [fetchEvents]);

    const handleFilterChange = (value) => {
        const next = value === 'all' ? '' : value;
        setEventTypeFilter(next);
        fetchEvents(next);
    };

    const handleRefresh = () => {
        fetchEvents(eventTypeFilter);
    };

    return (
        <div className="space-y-6" data-testid="company-security-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Lock className="w-5 h-5 text-primary" />
                        {t('company.tabs.security')}
                    </CardTitle>
                    <CardDescription>{t('company.security.intro')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Toolbar */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <Select
                            value={eventTypeFilter || 'all'}
                            onValueChange={handleFilterChange}
                        >
                            <SelectTrigger className="w-52" data-testid="audit-event-type-filter">
                                <SelectValue placeholder={t('audit.filter.all_events')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('audit.filter.all_events')}</SelectItem>
                                {AUDIT_EVENT_TYPES.map((et) => (
                                    <SelectItem key={et} value={et}>
                                        {t(`audit.event.${et}`, { defaultValue: et })}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefresh}
                            disabled={loading}
                            data-testid="audit-refresh-btn"
                        >
                            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                            {t('audit.refresh')}
                        </Button>
                    </div>

                    {/* Table */}
                    <AuditEventTable
                        events={events}
                        includeCompanyColumn={false}
                        loading={loading}
                        t={t}
                    />

                    {/* Limit note */}
                    <p className="text-xs text-muted-foreground">
                        {t('company.security.limit_note')}
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const VALID_COMPANY_TABS = ['profile', 'invoicing', 'branding', 'auth', 'integrations', 'security'];

export default function CompanyPage() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const requestedTab = searchParams.get('tab');
    const currentTab = VALID_COMPANY_TABS.includes(requestedTab) ? requestedTab : 'profile';

    const handleTabChange = (value) => {
        setSearchParams({ tab: value }, { replace: true });
    };

    return (
        <div className="space-y-6" data-testid="company-page">
            <div>
                <h1 className="font-heading text-4xl font-bold tracking-tight">
                    {t('company.page_title')}
                </h1>
                <p className="text-muted-foreground mt-1">{t('company.page_subtitle')}</p>
            </div>

            <Tabs value={currentTab} onValueChange={handleTabChange} className="space-y-4">
                <TabsList className="grid w-full grid-cols-6">
                    <TabsTrigger value="profile" data-testid="tab-company-profile">
                        <Building2 className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">{t('company.profile')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="invoicing" data-testid="tab-company-invoicing">
                        <Receipt className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">{t('company.invoicing.tab')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="branding" data-testid="tab-company-branding">
                        <Paintbrush className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">{t('company.branding')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="auth" data-testid="tab-company-auth">
                        <ShieldCheck className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">{t('company.auth')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="integrations" data-testid="tab-company-integrations">
                        <Puzzle className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">{t('company.integrations')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="security" data-testid="tab-company-security">
                        <Lock className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">{t('company.tabs.security')}</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="profile">
                    <CompanyProfileTab />
                </TabsContent>
                <TabsContent value="invoicing">
                    <InvoicingTab />
                </TabsContent>
                <TabsContent value="branding">
                    <BrandingTab />
                </TabsContent>
                <TabsContent value="auth">
                    <AuthAccessTab />
                </TabsContent>
                <TabsContent value="integrations">
                    <IntegrationsTab />
                </TabsContent>
                <TabsContent value="security">
                    <SecurityTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}
